// src/api/emptyResponse.js
//
// The API answers many "list" requests with HTTP 404 when there is simply nothing to list, e.g.
//   GET /Rooms/all/7        -> 404 "No rooms found for Hotel ID 7."
//   GET /Price/hotel/7      -> 404 { Message: "No prices found for HotelId 7." }
// For a screen that is an EMPTY LIST, not a failure. This turns those answers into `200 []` so every
// page and table renders its normal empty state, with no error toast.
//
// Deliberately narrow so real errors still surface:
//   - GET requests only
//   - the message must start with "No" and name a plural collection ("No bookings found ...")
//   - single-item lookups ("Guest not found", "Booking not found", "No KYC submission found") stay errors
//
// The proper long-term fix is for those endpoints to return 200 with an empty array; this is the safety net.

const COLLECTION_WORDS =
  /\b(bookings|rooms|statuses|prices|records|housekeepers|items|users|staff|requests|options|tasks|amenities|guests|services|templates|reports|promotions|logs)\b/i;

const messageOf = (data) => {
  if (typeof data === "string") return data;
  if (data && typeof data === "object") return data.message ?? data.Message ?? "";
  return "";
};

export const isEmptyListNotFound = (error) => {
  const res = error?.response;
  if (!res || res.status !== 404) return false;
  if (String(error?.config?.method || "get").toLowerCase() !== "get") return false;
  const msg = String(messageOf(res.data) || "").trim();
  return /^no\s.+\s(found|available)\b/i.test(msg) && COLLECTION_WORDS.test(msg);
};

export const emptyListResponse = (error) => ({
  ...error.response,
  status: 200,
  statusText: "OK",
  data: [],
  emptyFrom404: true,
});

/** Always an array: the value itself, or the first array found under the usual wrapper keys, or []. */
export const asList = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const k of keys.length ? keys : ["items", "data", "results", "list"]) {
    if (Array.isArray(data?.[k])) return data[k];
  }
  return [];
};
